import { z } from "zod";

import type { Order } from "~/v2/api/rest/account";
import type { ReplaceAtdOrderPayload } from "./api";

export const REPLACEABLE_ORDER_STATUSES = new Set(["new", "partially_filled"]);

const SUPPORTED_ASSET_CLASSES = new Set(["us_equity", "us_option", "crypto"]);
const SUPPORTED_ORDER_TYPES = new Set([
  "market",
  "limit",
  "stop",
  "stop_limit",
]);
const WHOLE_QUANTITY_ASSET_CLASSES = new Set(["us_equity", "us_option"]);
const CANCELLABLE_ORDER_STATUSES = new Set([
  "accepted",
  "accepted_for_bidding",
  "held",
  "new",
  "open",
  "partially_filled",
  "pending_cancel",
  "pending_new",
  "pending_replace",
  "pending_review",
  "stopped",
  "suspended",
]);

const nonEmptyString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

const normalizedString = (value: unknown): string =>
  nonEmptyString(value)?.toLowerCase() ?? "";

const TIME_IN_FORCE_VALUES = [
  "day",
  "gtc",
  "opg",
  "cls",
  "ioc",
  "fok",
] as const;

type TimeInForceValue = (typeof TIME_IN_FORCE_VALUES)[number];

/** Equity replacement accepts the full OMS set. */
const EQUITY_TIME_IN_FORCE_VALUES = TIME_IN_FORCE_VALUES;
/** Options accept only day/gtc (same as v2 OPTION_TIME_IN_FORCE). */
const OPTION_TIME_IN_FORCE_VALUES = ["day", "gtc"] as const satisfies readonly [
  TimeInForceValue,
  ...TimeInForceValue[],
];
/** Crypto accepts only gtc/ioc (same as v2 CRYPTO_TIME_IN_FORCE). */
const CRYPTO_TIME_IN_FORCE_VALUES = ["gtc", "ioc"] as const satisfies readonly [
  TimeInForceValue,
  ...TimeInForceValue[],
];

export const TIME_IN_FORCE_OPTIONS = [
  { label: "Day", value: "day" },
  { label: "GTC", value: "gtc" },
  { label: "OPG", value: "opg" },
  { label: "CLS", value: "cls" },
  { label: "IOC", value: "ioc" },
  { label: "FOK", value: "fok" },
] as const satisfies {
  label: string;
  value: TimeInForceValue;
}[];

export const getTimeInForceValuesForAssetClass = (
  assetClass: unknown,
): readonly [TimeInForceValue, ...TimeInForceValue[]] => {
  switch (normalizedString(assetClass)) {
    case "crypto":
      return CRYPTO_TIME_IN_FORCE_VALUES;
    case "us_option":
      return OPTION_TIME_IN_FORCE_VALUES;
    default:
      return EQUITY_TIME_IN_FORCE_VALUES;
  }
};

export const getTimeInForceOptions = (assetClass?: unknown) => {
  const allowed = new Set<string>(
    getTimeInForceValuesForAssetClass(assetClass),
  );
  return TIME_IN_FORCE_OPTIONS.filter((option) => allowed.has(option.value));
};

export const normalizeTimeInForce = (
  value: unknown,
  assetClass?: unknown,
): TimeInForceValue => {
  const allowed = getTimeInForceValuesForAssetClass(assetClass);
  const normalized = normalizedString(value);
  return (allowed as readonly string[]).includes(normalized)
    ? (normalized as TimeInForceValue)
    : allowed[0];
};

const timeInForceLabel = (value: string): string =>
  TIME_IN_FORCE_OPTIONS.find((option) => option.value === value)?.label ??
  value;

const isSimpleOrderClass = (value: unknown): boolean =>
  typeof value === "string" &&
  (value === "" || value.trim().toLowerCase() === "simple");

interface CanonicalDecimal {
  value: string;
  integer: string;
  fraction: string;
  negative: boolean;
}

const ORDINARY_DECIMAL_PATTERN = /^([+-]?)(?:(\d+)(?:\.(\d*))?|\.(\d+))$/;

const canonicalDecimal = (value: unknown): CanonicalDecimal | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const match = ORDINARY_DECIMAL_PATTERN.exec(value.trim());
  if (!match) {
    return undefined;
  }

  const sign = match[1] ?? "";
  const rawInteger = match[2] ?? "0";
  const rawFraction = match[3] ?? match[4] ?? "";
  const integer = rawInteger.replace(/^0+(?=\d)/, "");
  const fraction = rawFraction.replace(/0+$/, "");
  const unsignedValue = fraction ? `${integer}.${fraction}` : integer;
  const isZero = integer === "0" && fraction === "";
  const negative = sign === "-" && !isZero;

  return {
    value: negative ? `-${unsignedValue}` : unsignedValue,
    integer,
    fraction,
    negative,
  };
};

const isPositiveDecimal = (value: unknown): boolean => {
  const parsed = canonicalDecimal(value);
  return (
    parsed !== undefined &&
    !parsed.negative &&
    (parsed.integer !== "0" || parsed.fraction !== "")
  );
};

const decimalValuesEqual = (first: unknown, second: unknown): boolean => {
  const firstDecimal = canonicalDecimal(first);
  const secondDecimal = canonicalDecimal(second);
  return Boolean(
    firstDecimal && secondDecimal && firstDecimal.value === secondDecimal.value,
  );
};

const compareNonnegativeDecimals = (
  first: CanonicalDecimal,
  second: CanonicalDecimal,
): -1 | 0 | 1 | undefined => {
  if (first.negative || second.negative) {
    return undefined;
  }
  if (first.integer.length !== second.integer.length) {
    return first.integer.length < second.integer.length ? -1 : 1;
  }
  if (first.integer !== second.integer) {
    return first.integer < second.integer ? -1 : 1;
  }

  const fractionLength = Math.max(
    first.fraction.length,
    second.fraction.length,
  );
  const firstFraction = first.fraction.padEnd(fractionLength, "0");
  const secondFraction = second.fraction.padEnd(fractionLength, "0");
  if (firstFraction === secondFraction) {
    return 0;
  }
  return firstFraction < secondFraction ? -1 : 1;
};

export type ReplacementPriceField = "limitPrice" | "stopPrice";

export interface OrderReplacementFormValues {
  qty: string;
  limitPrice: string;
  stopPrice: string;
  timeInForce: string;
}

export interface OrderReplacementChange {
  field: keyof ReplaceAtdOrderPayload;
  label: string;
  before: string;
  after: string;
}

export type OrderReplacementEligibility =
  | { eligible: true; reason?: undefined }
  | { eligible: false; reason: string };

export const getReplacementOrderType = (order: Order): string =>
  normalizedString(order.order_type || order.type);

export const getApplicableReplacementPriceFields = (
  order: Order,
): ReplacementPriceField[] => {
  switch (getReplacementOrderType(order)) {
    case "limit":
      return ["limitPrice"];
    case "stop":
      return ["stopPrice"];
    case "stop_limit":
      return ["limitPrice", "stopPrice"];
    default:
      return [];
  }
};

export const getOrderReplacementEligibility = (
  order: Order,
): OrderReplacementEligibility => {
  const status = normalizedString(order.status);
  if (!REPLACEABLE_ORDER_STATUSES.has(status)) {
    return {
      eligible: false,
      reason: "Only new or partially filled orders can be edited.",
    };
  }

  if (!nonEmptyString(order.id)) {
    return { eligible: false, reason: "Order ID is unavailable." };
  }

  const assetClass = normalizedString(order.asset_class);
  if (!SUPPORTED_ASSET_CLASSES.has(assetClass)) {
    return {
      eligible: false,
      reason: "This asset class is not supported for editing.",
    };
  }

  if (!isSimpleOrderClass(order.order_class)) {
    return {
      eligible: false,
      reason: "Bracket, OCO, OTO, and other linked orders cannot be edited.",
    };
  }

  if (
    order.legs != null &&
    (!Array.isArray(order.legs) || order.legs.length > 0)
  ) {
    return {
      eligible: false,
      reason: "Multi-leg orders cannot be edited.",
    };
  }

  if (nonEmptyString(order.notional)) {
    return {
      eligible: false,
      reason: "Notional orders cannot be edited.",
    };
  }

  if (order.advanced_instructions != null) {
    return {
      eligible: false,
      reason: "Orders with advanced instructions cannot be edited.",
    };
  }

  const orderType = getReplacementOrderType(order);
  if (
    orderType === "trailing_stop" ||
    nonEmptyString(order.trail_price) ||
    nonEmptyString(order.trail_percent)
  ) {
    return {
      eligible: false,
      reason: "Trailing stop orders cannot be edited.",
    };
  }
  if (!SUPPORTED_ORDER_TYPES.has(orderType)) {
    return {
      eligible: false,
      reason: "This order type is not supported for editing.",
    };
  }

  if (
    !nonEmptyString(order.symbol) ||
    !nonEmptyString(order.side) ||
    !nonEmptyString(order.status)
  ) {
    return {
      eligible: false,
      reason: "Order context is incomplete and cannot be edited safely.",
    };
  }

  const quantity = canonicalDecimal(order.qty);
  const filledQuantity = canonicalDecimal(order.filled_qty);
  if (
    quantity === undefined ||
    !isPositiveDecimal(order.qty) ||
    filledQuantity === undefined ||
    filledQuantity.negative
  ) {
    return {
      eligible: false,
      reason: "Quantity data is incomplete and cannot be edited safely.",
    };
  }
  const quantityComparedToFilled = compareNonnegativeDecimals(
    quantity,
    filledQuantity,
  );
  if (quantityComparedToFilled === undefined || quantityComparedToFilled <= 0) {
    return {
      eligible: false,
      reason: "Quantity must remain greater than the filled quantity.",
    };
  }
  if (
    WHOLE_QUANTITY_ASSET_CLASSES.has(assetClass) &&
    quantity.fraction !== ""
  ) {
    return {
      eligible: false,
      reason:
        assetClass === "us_equity"
          ? "Fractional US equity orders cannot be edited."
          : "Option quantity must be a whole number of contracts.",
    };
  }

  if (
    getApplicableReplacementPriceFields(order).some((field) => {
      const value =
        field === "limitPrice" ? order.limit_price : order.stop_price;
      return !isPositiveDecimal(value);
    })
  ) {
    return {
      eligible: false,
      reason:
        "Applicable price data is incomplete and cannot be edited safely.",
    };
  }

  return { eligible: true };
};

export const getOrderCancellationDisabledReason = (
  order: Order,
): string | undefined => {
  if (!nonEmptyString(order.id)) {
    return "Order ID is unavailable.";
  }

  return CANCELLABLE_ORDER_STATUSES.has(normalizedString(order.status))
    ? undefined
    : "This order is no longer open for cancellation.";
};

export const isOrderStatusCancellable = (status: unknown): boolean =>
  CANCELLABLE_ORDER_STATUSES.has(normalizedString(status));

export const createOrderReplacementInitialValues = (
  order: Order,
): OrderReplacementFormValues => ({
  qty: nonEmptyString(order.qty) ?? "",
  limitPrice: nonEmptyString(order.limit_price) ?? "",
  stopPrice: nonEmptyString(order.stop_price) ?? "",
  timeInForce: normalizeTimeInForce(order.time_in_force, order.asset_class),
});

const requiredPositiveMessage = (label: string) =>
  `${label} must be a positive number.`;

export const createOrderReplacementSchema = (order: Order) =>
  z
    .object({
      qty: z.string(),
      limitPrice: z.string(),
      stopPrice: z.string(),
      timeInForce: z.enum(getTimeInForceValuesForAssetClass(order.asset_class)),
    })
    .superRefine((values, context) => {
      const quantity = canonicalDecimal(values.qty);
      const filledQuantity = canonicalDecimal(order.filled_qty);
      const assetClass = normalizedString(order.asset_class);

      if (!quantity || !isPositiveDecimal(values.qty)) {
        context.addIssue({
          code: "custom",
          message: requiredPositiveMessage("Quantity"),
          path: ["qty"],
        });
      } else {
        const quantityComparedToFilled = filledQuantity
          ? compareNonnegativeDecimals(quantity, filledQuantity)
          : undefined;
        if (
          quantityComparedToFilled === undefined ||
          quantityComparedToFilled <= 0
        ) {
          context.addIssue({
            code: "custom",
            message: "Quantity must be greater than the filled quantity.",
            path: ["qty"],
          });
        }
        if (
          WHOLE_QUANTITY_ASSET_CLASSES.has(assetClass) &&
          quantity.fraction !== ""
        ) {
          context.addIssue({
            code: "custom",
            message:
              assetClass === "us_option"
                ? "Option quantity must be a whole number of contracts."
                : "US equity quantity must be a whole number.",
            path: ["qty"],
          });
        }
      }

      for (const field of getApplicableReplacementPriceFields(order)) {
        if (!isPositiveDecimal(values[field])) {
          context.addIssue({
            code: "custom",
            message: requiredPositiveMessage(
              field === "limitPrice" ? "Limit price" : "Stop price",
            ),
            path: [field],
          });
        }
      }
    });

export const buildOrderReplacementPayload = (
  order: Order,
  values: OrderReplacementFormValues,
): ReplaceAtdOrderPayload => {
  const payload: ReplaceAtdOrderPayload = {};
  const quantity = canonicalDecimal(values.qty);
  if (quantity && !decimalValuesEqual(quantity.value, order.qty)) {
    payload.qty = quantity.value;
  }

  const applicableFields = getApplicableReplacementPriceFields(order);
  const limitPrice = canonicalDecimal(values.limitPrice);
  if (
    limitPrice &&
    applicableFields.includes("limitPrice") &&
    !decimalValuesEqual(limitPrice.value, order.limit_price)
  ) {
    payload.limit_price = limitPrice.value;
  }
  const stopPrice = canonicalDecimal(values.stopPrice);
  if (
    stopPrice &&
    applicableFields.includes("stopPrice") &&
    !decimalValuesEqual(stopPrice.value, order.stop_price)
  ) {
    payload.stop_price = stopPrice.value;
  }

  const timeInForce = normalizeTimeInForce(
    values.timeInForce,
    order.asset_class,
  );
  if (
    timeInForce !== normalizeTimeInForce(order.time_in_force, order.asset_class)
  ) {
    payload.time_in_force = timeInForce;
  }

  return payload;
};

export const buildOrderReplacementChanges = (
  order: Order,
  payload: ReplaceAtdOrderPayload,
): OrderReplacementChange[] => {
  const changes: OrderReplacementChange[] = [];
  if (payload.qty !== undefined) {
    changes.push({
      field: "qty",
      label: "Quantity",
      before: nonEmptyString(order.qty) ?? "—",
      after: payload.qty,
    });
  }
  if (payload.limit_price !== undefined) {
    changes.push({
      field: "limit_price",
      label: "Limit price",
      before: nonEmptyString(order.limit_price) ?? "—",
      after: payload.limit_price,
    });
  }
  if (payload.stop_price !== undefined) {
    changes.push({
      field: "stop_price",
      label: "Stop price",
      before: nonEmptyString(order.stop_price) ?? "—",
      after: payload.stop_price,
    });
  }
  if (payload.time_in_force !== undefined) {
    changes.push({
      field: "time_in_force",
      label: "Time in force",
      before: timeInForceLabel(
        normalizeTimeInForce(order.time_in_force, order.asset_class),
      ),
      after: timeInForceLabel(payload.time_in_force),
    });
  }
  return changes;
};

// Intentional duplicate of OrdersWidget.utils's ORDERS_UNAVAILABLE_VALUE.
// Do not import it from there: OrdersWidget.utils already value-imports
// replacement helpers from this module, so importing it back here would
// create OrderReplacementDialog.utils -> OrdersWidget.utils ->
// OrderReplacementDialog.utils. A drift test in OrderReplacementDialog.utils.test.ts
// asserts this value stays equal to ORDERS_UNAVAILABLE_VALUE.
const ORDER_REPLACEMENT_UNAVAILABLE_VALUE = "—";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const finiteNumber = (value: string): number | undefined => {
  const parsed = Number(value.replaceAll(",", ""));
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const estimateReplaceCost = (
  qty: string,
  limitPrice: string,
): string => {
  const quantity = finiteNumber(qty);
  const limit = finiteNumber(limitPrice);
  if (quantity === undefined || limit === undefined) {
    return ORDER_REPLACEMENT_UNAVAILABLE_VALUE;
  }
  return currencyFormatter.format(quantity * limit);
};
